// src/views/knowledge-base/WalkthroughPlayer.ts
// Scribe-style custom walkthrough overlay (no driver.js).
import './walkthrough.css'
import type { KbStep } from './types'
import { coalesceSteps } from './steps-utils'
import { FIXTURES, fixturePlanFor, spliceFixturesIntoUnified, buildTestSubProjectDetail, isTutSubProjectDetailUrl, type EphemeralFixture, type FixtureKind } from './tutorialFixtures'
import { isTourIdSelector, isAddressSuggestionStep, resolveSuggestionForStep, isOwnerSelectStep, resolveOwnerSelectForStep, type SuggestionDomRoot } from '../../lib/walkthrough/resolveFallback'
import { rectKey, hasRectMoved } from '../../lib/walkthrough/repositionTracking'
import { deriveAction, shouldAllowManualAdvance, fieldSetShouldAdvance, selectionIsTruthy, type ActionKind } from '../../lib/walkthrough/actionClassify'
import { waitForElement } from '../../lib/walkthrough/waitForElement'

/**
 * Walkthrough player options.
 *
 * All walkthroughs run in LIVE mode: writes hit the backend for real, but the
 * player snapshots each touched parent record on the way in and replays the
 * snapshot back to the API on exit/done so the underlying data ends up in its
 * pre-run state. There is no test/preview mode — there is just one Run.
 *
 * Integration with Tutorial Creation Mode (`window.__originalFetchKbTC`): when
 * that mode is active, the global fetch is already patched to block writes, so
 * we skip our own snapshot/restore wiring (it would no-op anyway).
 */
export type WalkthroughOptions = {
  steps: KbStep[]
  navigate: (to: string) => void
  onExit?: () => void
  onDone?: () => void
  // Tutorial identity — used to look up declarative ephemeral fixtures
  // (e.g. a tutorial-only "Johnny Test" contact). Optional; absent ⇒ no fixtures.
  tutorialTitle?: string | null
  tutorialId?: number | null
}

const QUOTED_RE = /[""""'']([^""""''\n]+)[""""'']/  // matches "...", '...', and smart-quotes

// `isTourIdSelector` (stable `[data-tour-id="…"]` hook detection) lives in the
// pure, unit-guarded helper src/lib/walkthrough/resolveFallback.ts. These hooks
// are authored by us against real DOM nodes (added in App.tsx etc.), so when one
// resolves we TRUST it outright — no text cross-check. This is what makes
// icon-only buttons (the "+" create button has no text) advance reliably.
function extractExpectedValue(step: KbStep): string | null {
  // Explicit step.expected_value still wins if present (manual override).
  if (step.expected_value && step.expected_value.trim()) return step.expected_value.trim()
  if (!step.text) return null
  const m = step.text.match(QUOTED_RE)
  return m ? m[1].trim() : null
}

const NAV_RE = /^\s*Navigate to\s+(\S+)/i
// ACTION_RE, ActionKind and deriveAction now live in the pure, node-testable
// helper src/lib/walkthrough/actionClassify.ts (imported above) so the field-set
// ('set') classification + dead-end guard are unit-guarded.

function resolveElementWithObserver(
  step: KbStep,
  timeoutMs: number,
  registerDisconnect: (fn: () => void) => void,
): Promise<HTMLElement | null> {
  // Wait/observe/timeout/cleanup logic lives in the pure, node-testable
  // waitForElement helper (src/lib/walkthrough/waitForElement.ts). This wires it
  // to the real MutationObserver + setTimeout. Behavior: resolve immediately if
  // present, else re-check on every DOM mutation (so DYNAMIC targets — the
  // address-suggestion rows and the owner-option rows that render only when the
  // SELECT OWNERS dropdown opens — get caught the instant they appear), else
  // resolve null after timeoutMs (caller keeps its manual-continue fallback).
  return waitForElement<HTMLElement>({
    resolveNow: () => resolveElement(step),
    observe: (onMutation) => {
      let observer: MutationObserver | null = null
      try {
        observer = new MutationObserver(() => onMutation())
        observer.observe(document.body, { childList: true, subtree: true, attributes: false })
      } catch { /* */ }
      return () => { if (observer) { try { observer.disconnect() } catch { /* */ } observer = null } }
    },
    schedule: (cb, ms) => {
      const timer = window.setTimeout(cb, ms)
      return () => { try { clearTimeout(timer) } catch { /* */ } }
    },
    timeoutMs,
    registerCancel: registerDisconnect,
  })
}

function findInputWrapper(input: HTMLElement): HTMLElement | null {
  let cur: HTMLElement | null = input.parentElement
  for (let depth = 0; cur && depth < 4; depth++) {
    const cs = window.getComputedStyle(cur)
    if (cs.position === 'relative' || cs.position === 'absolute') return cur
    cur = cur.parentElement
  }
  return input.parentElement
}

// Exported so the live-browser regression harness
// (scripts/verify-deal-row-tour.mjs) can drive the EXACT shipped resolver
// against faithful deals-row markup. No behavior change; tree-shaken in prod.
export function resolveElement(step: KbStep): HTMLElement | null {
  // Try the recorded selector and xpath first — they're the most precise
  // signals available. Text-match is a fallback only when neither resolves.
  // We also cross-check: if a target_text is known and the matched element
  // doesn't contain that text, the selector is matching a stale duplicate
  // (e.g. Scribe captured ".nav-item" which now matches every sidebar item) —
  // fall through to the text-match path so the right element is picked.
  const isClickStep = deriveActionKindLite(step) === 'click'
  const quoted = isClickStep ? step.text?.match(QUOTED_RE)?.[1]?.trim() : undefined
  const wantText = (step.target_text || quoted || '').trim()
  const elemMatchesWantText = (el: HTMLElement) => {
    if (!wantText) return true
    const t = (el.textContent || '').trim().toLowerCase().replace(/\s+/g, ' ')
    const n = wantText.toLowerCase().replace(/\s+/g, ' ')
    return t === n || t.includes(n)
  }
  // Address-autocomplete suggestion step (tutorial 6, "Click "123 Main Street"
  // suggestion"). This MUST run BEFORE the selector/xpath/target_text branches
  // below, because the real step has an EMPTY selector AND empty xpath and would
  // otherwise resolve via the page-wide target_text scan — which is exactly what
  // hijacked the spotlight onto the dashboard's static "123 Main Street,
  // Windermere, FL" Delivery-watch card behind the Create-New panel.
  //
  // Detection keys on the visible step TEXT (/suggestion/i) — NOT the selector
  // (the 2nd-round bug). resolveSuggestionForStep is SCOPED STRICTLY to the
  // dropdown container: it returns a row INSIDE the suggestion dropdown (matching
  // the quoted/target_text address if present, else the first row) or null
  // (dropdown not open → the observer retries). There is NO page-wide fallback
  // for this step, so it can never select an element outside the panel.
  // See src/lib/walkthrough/resolveFallback.ts + its test.
  if (isAddressSuggestionStep(step)) {
    const row = resolveSuggestionForStep(step, document as unknown as SuggestionDomRoot)
    return (row as unknown as HTMLElement) || null
  }
  // Project-record OWNER-select step ("Select "Maxwell Ritchie" as the owner").
  // MUST run BEFORE the selector/xpath/target_text branches: this step has
  // selector=NULL and a stale `<select>` xpath (the owner control is now a custom
  // button + portal picker), so it would otherwise fall through to the page-wide
  // quoted-text scan that grabbed the TOP-NAV user-menu button (which reads the
  // logged-in user's name, "Maxwell Ritchie"). resolveOwnerSelectForStep is
  // scoped strictly to the owner control / owner-options picker — never page-wide.
  // It returns null only when the owner control isn't on this page, in which case
  // we continue to the normal resolution below. See resolveFallback.ts + its test.
  if (isOwnerSelectStep(step)) {
    const ownerEl = resolveOwnerSelectForStep(step, document as unknown as SuggestionDomRoot)
    if (ownerEl) return ownerEl as unknown as HTMLElement
  }
  if (step.selector) {
    try {
      // Stable tour hooks win outright — we authored them against the real
      // element, so a match is authoritative even when the element has no text
      // (icon-only buttons) or its text disagrees with a stale recorded value.
      if (isTourIdSelector(step.selector)) {
        const hook = document.querySelector<HTMLElement>(step.selector)
        if (hook) return hook
        // Hook not in the DOM yet (e.g. a transient address-autocomplete
        // suggestion row that only exists while the dropdown is open). A stable
        // data-tour-id is AUTHORITATIVE — return null instead of falling through
        // to the global quoted-text fallback below. resolveElementWithObserver
        // keeps retrying on every DOM mutation until the real hook appears, and
        // showStep installs a manual-continue delegated listener meanwhile.
        // Falling through here was the bug: tutorial 6's "Click "123 Main Street"
        // suggestion" step text-matched the dashboard's static, clickable
        // Delivery-watch card ("123 Main Street, Windermere, FL 34786") sitting
        // behind the create panel — an element entirely outside the tour's scope.
        return null
      } else {
        // Prefer a same-selector match whose text contains the wanted text
        // before falling back to the first match.
        const all = Array.from(document.querySelectorAll<HTMLElement>(step.selector))
        const withText = wantText ? all.find(elemMatchesWantText) : null
        const el = withText || all[0] || null
        if (el && elemMatchesWantText(el)) return el
      }
    } catch { /* */ }
  }
  if (step.xpath) {
    try {
      const r = document.evaluate(step.xpath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null)
      const node = r.singleNodeValue as HTMLElement | null
      if (node && elemMatchesWantText(node)) return node
    } catch { /* */ }
  }
  // Text-match fallback — also reached when the selector/xpath only matched
  // an element whose text disagrees with target_text.
  if (quoted && quoted.length >= 2) {
    const byText = findClickableByText(quoted)
    if (byText) return byText
  }
  if (step.target_text && step.tag) {
    const els = Array.from(document.querySelectorAll<HTMLElement>(step.tag.toLowerCase()))
    const match = els.find(el => el.textContent?.trim() === step.target_text!.trim())
    if (match) return match
  }
  if (wantText) {
    const byText = findClickableByText(wantText)
    if (byText) return byText
  }
  return null
}

function deriveActionKindLite(step: KbStep): ActionKind {
  return deriveAction(step).kind
}

function textMatches(el: HTMLElement, needle: string): boolean {
  const t = (el.textContent || '').trim().toLowerCase().replace(/\s+/g, ' ')
  const n = needle.trim().toLowerCase().replace(/\s+/g, ' ')
  return t === n || t.includes(n)
}

// Visible overlay/modal containers — text-match results inside these win over
// background-page matches so e.g. clicking "TEST DEAL" suggestion in a Create
// New panel doesn't resolve to a "TEST DEAL" link elsewhere on the page.
const OVERLAY_SELECTORS = [
  '.create-panel',
  '.create-panel-overlay',
  '.sub-admin-dialog',
  '.modal',
  '.dialog',
  '[role="dialog"]',
  '[role="alertdialog"]',
  '.kbadmin', // not user-facing but harmless
  '.cmd-results-scroll',
  '.popover-content',
  '.sub-portal-role-dropdown', // role switcher dropdown in subcontractor portal
  '.cp-multi-row',             // create-panel email/phone multi-row inputs
].join(',')

function findOpenOverlay(): HTMLElement | null {
  const overlays = Array.from(document.querySelectorAll<HTMLElement>(OVERLAY_SELECTORS))
  // Pick the LAST one that's visible (top-most modal in stack).
  for (let i = overlays.length - 1; i >= 0; i--) {
    const el = overlays[i]
    if (el.offsetWidth > 0 && el.offsetHeight > 0) return el
  }
  return null
}

function findClickableByTextInScope(n: string, scope: HTMLElement): HTMLElement | null {
  // Inside a known overlay scope: match exact text first, then partial, then
  // walk up to nearest cursor:pointer ancestor (handles custom suggestion rows).
  const candidates = Array.from(scope.querySelectorAll<HTMLElement>('button, a, [role="button"], [role="menuitem"], li, div, span, p, td'))
  const exact = candidates.filter(el => (el.textContent || '').trim().toLowerCase().replace(/\s+/g, ' ') === n)
  if (exact.length) {
    const deepest = exact.reduce((d, e) => {
      const dDepth = (() => { let x: HTMLElement | null = d, c = 0; while (x) { c++; x = x.parentElement } return c })()
      const eDepth = (() => { let x: HTMLElement | null = e, c = 0; while (x) { c++; x = x.parentElement } return c })()
      return eDepth > dDepth ? e : d
    })
    let cur: HTMLElement | null = deepest
    for (let i = 0; cur && i < 6; i++) {
      try { if (window.getComputedStyle(cur).cursor === 'pointer') return cur } catch { /* */ }
      cur = cur.parentElement
    }
    return deepest
  }
  if (n.length <= 6) return null
  const partial = candidates.find(el => {
    const t = (el.textContent || '').trim().toLowerCase().replace(/\s+/g, ' ')
    return t.length > 0 && t.length < n.length * 4 && t.includes(n)
  })
  if (!partial) return null
  let cur: HTMLElement | null = partial
  for (let i = 0; cur && i < 6; i++) {
    try { if (window.getComputedStyle(cur).cursor === 'pointer') return cur } catch { /* */ }
    cur = cur.parentElement
  }
  return partial
}

function findClickableByText(needle: string): HTMLElement | null {
  const n = needle.trim().toLowerCase().replace(/\s+/g, ' ')
  const overlay = findOpenOverlay()
  // If a modal/panel is open, prefer matches WITHIN it (scoped search), then
  // fall back to global. Avoids picking up text from background page.
  if (overlay) {
    const scoped = findClickableByTextInScope(n, overlay)
    if (scoped) return scoped
  }
  const candidates = Array.from(document.querySelectorAll<HTMLElement>('button, a, [role="button"], [role="menuitem"], li, .nav-item, .nav-label'))
  // Prefer EXACT text match on smallest element (deepest leaf).
  const exact = candidates.filter(el => (el.textContent || '').trim().toLowerCase().replace(/\s+/g, ' ') === n)
  if (exact.length) {
    // Pick the deepest (most specific) one.
    return exact.reduce((deepest, el) => {
      let depth = 0
      let cur: HTMLElement | null = el
      while (cur) { depth++; cur = cur.parentElement }
      let dDepth = 0
      let dCur: HTMLElement | null = deepest
      while (dCur) { dDepth++; dCur = dCur.parentElement }
      return depth > dDepth ? el : deepest
    })
  }
  // For short queries (≤6 chars like "Assign", "OK", "Save"), require EXACT
  // match — partial-includes would bring in "Assigned", "Assign owner first",
  // etc. and confuse users. Longer queries use contains match.
  if (n.length <= 6) return null
  // Then a contains match — but skip very large elements (nav containers).
  const partial = candidates.filter(el => {
    const t = (el.textContent || '').trim().toLowerCase().replace(/\s+/g, ' ')
    return t.length > 0 && t.length < n.length * 4 && t.includes(n)
  })
  if (partial.length) return partial[0]
  // Final fallback: any element with the text, then walk up to nearest
  // cursor:pointer ancestor (suggestion list rows in custom dropdowns).
  const all = Array.from(document.querySelectorAll<HTMLElement>('div, span, p, td, li'))
  const textMatch = all.find(el => {
    const t = (el.textContent || '').trim().toLowerCase().replace(/\s+/g, ' ')
    return t.length > 0 && t.length < n.length * 6 && (t === n || t.includes(n))
  })
  if (!textMatch) return null
  let cur: HTMLElement | null = textMatch
  for (let i = 0; cur && i < 6; i++) {
    try {
      if (window.getComputedStyle(cur).cursor === 'pointer') return cur
    } catch { /* */ }
    cur = cur.parentElement
  }
  return textMatch
}

function pathFromUrl(url: string): string {
  try {
    const u = new URL(url, window.location.origin)
    return (u.hash && u.hash.startsWith('#')) ? u.hash.replace(/^#/, '') || '/' : (u.pathname + u.search)
  } catch { return url }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!))
}

export function startWalkthrough(opts: WalkthroughOptions): { cancel: () => void } {
  try { (window as any).__kbWalkthroughActive = true; window.dispatchEvent(new CustomEvent('kb-walkthrough-changed')) } catch { /* */ }
  // DEFENSE-IN-DEPTH for the Create-draft leak: snapshot the exact drafts blob
  // at walkthrough START and restore it verbatim on cleanup, so any draft
  // created DURING the tutorial vanishes while pre-existing user drafts survive.
  // (Primary guard is the __kbWalkthroughActive short-circuit in App.tsx's
  // autosave effect; this restore covers any write that slips through.)
  const CREATE_DRAFTS_KEY = 'crm.createDrafts.v1'
  let createDraftsSnapshot: string | null = null
  try { createDraftsSnapshot = window.localStorage.getItem(CREATE_DRAFTS_KEY) } catch { /* */ }
  let cancelled = false
  let i = 0
  // Number of leading Navigate steps consumed silently before the first visible step.
  let consumed = 0

  // Clear an input/textarea so React's controlled state syncs to empty.
  const clearInputElement = (el: HTMLElement) => {
    try {
      if (!document.body.contains(el)) return
      const tag = (el.tagName || '').toUpperCase()
      if (tag !== 'INPUT' && tag !== 'TEXTAREA') return
      const proto = tag === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype
      const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set
      if (setter) setter.call(el, '')
      else (el as HTMLInputElement).value = ''
      el.dispatchEvent(new Event('input', { bubbles: true }))
      el.dispatchEvent(new Event('change', { bubbles: true }))
    } catch { /* */ }
  }

  // Build root
  const root = document.createElement('div')
  root.id = 'kb-walk-root'
  document.body.appendChild(root)

  // Live walkthrough: writes pass through to the backend, but we snapshot the
  // affected parent record on the way in so we can replay it back on exit/done
  // and leave the data where we found it.
  //
  // Integration with Tutorial Creation Mode (useTutorialCreationMode.ts): if
  // that mode is active, window.__originalFetchKbTC is set and the global
  // fetch is already patched to block writes. Skip our snapshot/restore in
  // that case — the writes never reach the backend, so there's nothing to
  // snapshot and nothing to restore.
  const tutorialCreationActive = !!(window as any).__originalFetchKbTC
  const originalFetch = window.fetch.bind(window)
  const blockedMethods = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

  // ── Ephemeral tutorial-only fixtures ───────────────────────────────────────
  // A tutorial may declare client-only fake records (e.g. "Johnny Test") that
  // are spliced into GET list/record responses while the tutorial runs and
  // vanish on exit. They NEVER hit the backend (writes stay blocked) and are
  // tagged `_ephemeral`/`_tutorial` + `TUT-`-prefixed id so they can't leak.
  const fixturePlan = fixturePlanFor({ title: opts.tutorialTitle, id: opts.tutorialId })
  const activeFixtures = new Map<string, EphemeralFixture>()
  const activateFixture = (key: string) => {
    const fx = FIXTURES[key]
    if (fx) activeFixtures.set(fx.key, fx)
  }
  // Pre-injected fixtures show from the first frame of the walkthrough.
  if (fixturePlan?.preInject) for (const k of fixturePlan.preInject) activateFixture(k)
  // Fixtures that should appear only after their matching blocked create POST.
  const matchCreateFixture = (url: string): EphemeralFixture | null => {
    if (!fixturePlan?.onCreate) return null
    for (const k of fixturePlan.onCreate) {
      const fx = FIXTURES[k]
      if (fx && fx.matchesCreate(url)) return fx
    }
    return null
  }
  // GET list endpoints whose JSON array we splice fixtures into.
  const listKindForUrl = (pathname: string): FixtureKind | null => {
    if (/\/api\/crm\/clients\/?$/i.test(pathname)) return 'contact'
    if (/\/api\/crm\/leads\/?$/i.test(pathname)) return 'lead'
    if (/\/api\/crm\/deals\/?$/i.test(pathname)) return 'deal'
    if (/\/api\/crm\/projects\/?$/i.test(pathname)) return 'project'
    return null
  }
  // GET single-record endpoint for an ACTIVE fixture id (so it's openable).
  const recordFixtureForUrl = (pathname: string): EphemeralFixture | null => {
    const m = pathname.match(/\/api\/crm\/(clients|leads|deals|projects)\/([^/]+)\/?$/i)
    if (!m) return null
    const id = decodeURIComponent(m[2])
    for (const fx of activeFixtures.values()) if (fx.id === id) return fx
    return null
  }
  const jsonResponse = (obj: unknown) =>
    new Response(JSON.stringify(obj), { status: 200, headers: { 'Content-Type': 'application/json' } })
  // Splice active fixtures of `kind` into a real list response's JSON.
  const spliceListResponse = async (real: Response, kind: FixtureKind, searchTerm: string): Promise<Response> => {
    try {
      const ct = real.headers.get('content-type') || ''
      if (!real.ok || !ct.includes('application/json')) return real
      const data: any = await real.clone().json()
      // NOTE: the projects LIST returns its rows under `data` (not `projects`).
      const arrKey = kind === 'contact' ? 'clients' : kind === 'deal' ? 'deals' : kind === 'project' ? 'data' : 'leads'
      if (!Array.isArray(data?.[arrKey])) return real
      const term = (searchTerm || '').trim().toLowerCase()
      const additions: Record<string, unknown>[] = []
      for (const fx of activeFixtures.values()) {
        if (fx.kind !== kind) continue
        const rec = fx.build()
        // Honor an active search filter so the fixture doesn't surface under an
        // unrelated query — it should only show when the search would match it.
        if (term) {
          const hay = JSON.stringify(rec).toLowerCase()
          if (!hay.includes(term)) continue
        }
        // Don't double-inject if a real row with this id somehow exists.
        const idKey = kind === 'contact' ? 'unified_client_id' : 'id'
        if (data[arrKey].some((r: any) => String(r?.[idKey]) === String(rec[idKey]))) continue
        additions.push(rec)
      }
      if (additions.length === 0) return real
      data[arrKey] = [...additions, ...data[arrKey]]
      if (typeof data.total === 'number') data.total += additions.length
      return jsonResponse(data)
    } catch {
      return real
    }
  }
  const writePathRe = /\/api\/(crm|sub-admin|hr-admin|calendar|meetings|tasks|tickets|inspections|notes|activities|emails|email|m365|outlook|places|hr|deals|leads|clients|companies|contacts)\b/i
  // Tracks LIVE-mode writes so we can reverse them on walkthrough exit/done.
  // For PUT/PATCH: originalBody is the pre-write GET snapshot we'll re-PUT to snapshotUrl
  //                (which may be the parent record URL — not the exact write URL — when
  //                the write hits a sub-resource like /projects/:id/owner).
  // For DELETE:    originalBody is the pre-delete GET snapshot we'll re-POST to parent.
  // For POST:      createdId is the new record's id, which we'll DELETE on cleanup.
  type WriteEntry = { method: string; url: string; snapshotUrl?: string; originalBody?: any; createdId?: string | number; body?: any }
  const writeLog: WriteEntry[] = []
  // Track in-flight POST/PUT/PATCH/DELETE Promise so cleanup can await them
  // before running. Without this, done() may fire 80ms after a Save click
  // while the actual POST is still mid-flight — cleanup runs with empty
  // createdRecords, then the contact persists.
  const inFlightWrites = new Set<Promise<unknown>>()
  const waitForWrites = async (timeoutMs = 8000) => {
    if (inFlightWrites.size === 0) return
    await Promise.race([
      Promise.allSettled(Array.from(inFlightWrites)),
      new Promise(resolve => setTimeout(resolve, timeoutMs)),
    ])
  }
  // Tracks created records by entity-key for the final hard-delete cleanup pass
  // via POST /api/kb/cleanup. The archive endpoints used by restoreWrites() only
  // soft-archive — this pass guarantees walkthrough-created rows are GONE.
  const createdRecords: Record<string, (string | number)[]> = {}
  const classifyCreatedUrl = (url: string): string | null => {
    try {
      const path = new URL(url, window.location.origin).pathname
      // Match by the LAST collection name segment so nested paths like
      // /api/crm/deals/123/projects classify as crm_projects (not deals).
      const m = path.match(/\/(clients|contacts|leads|deals|projects|tickets|tasks|notes|activities|meetings|calls|emails)\/?$/i)
      if (!m) return null
      const last = m[1].toLowerCase()
      if (last === 'clients' || last === 'contacts') return 'contacts'
      if (last === 'leads') return 'leads'
      if (last === 'deals') return 'deals'
      if (last === 'tickets') return 'tickets'
      if (last === 'tasks') return 'tasks'
      if (last === 'notes') return 'notes'
      if (last === 'activities') return 'activities'
      if (last === 'meetings') return 'meetings'
      if (last === 'calls') return 'calls'
      if (last === 'emails') return 'emails'
      if (last === 'projects') {
        // Disambiguate: sub-admin/* → sub_projects, otherwise crm_projects.
        if (/\/api\/sub-admin\//i.test(path)) return 'sub_projects'
        return 'crm_projects'
      }
      return null
    } catch { return null }
  }
  const hardCleanup = async () => {
    const hasAny = Object.values(createdRecords).some(arr => arr && arr.length > 0)
    if (!hasAny) return
    try {
      const res = await originalFetch('/api/kb/cleanup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createdRecords),
      })
      if (res.ok) {
        const json = await res.json().catch(() => null)
        console.info('[walkthrough] hard-cleanup result:', json)
      } else {
        console.warn('[walkthrough] hard-cleanup non-OK status:', res.status)
      }
    } catch (e) {
      console.warn('[walkthrough] hard-cleanup threw', e)
    }
  }
  // De-dupe parent-record snapshots: only snapshot a given parent URL once per walkthrough.
  // Subsequent writes to the same parent reuse the first snapshot (so a single restore
  // PUT brings back ALL fields touched during the walkthrough).
  const snapshotByUrl = new Map<string, any>()
  // Walk up an /api/<entity>/<id>/<sub>/... URL trying GET at each level until we
  // find one that returns valid JSON. Returns { snapshotUrl, body } or null.
  const findSnapshot = async (writeUrl: string, opts?: { skipExact?: boolean }): Promise<{ snapshotUrl: string; body: any } | null> => {
    let u: URL
    try { u = new URL(writeUrl, window.location.origin) } catch { return null }
    const search = u.search
    const segments = u.pathname.split('/').filter(Boolean)
    // For nested-collection POSTs, the exact write URL has its own GET that
    // returns a LIST of children (e.g. GET /deals/:id/projects → list of
    // projects), not the parent record we want to snapshot. skipExact skips
    // the bottom level so we land on the parent (/deals/:id) instead.
    const startLen = opts?.skipExact ? Math.max(3, segments.length - 1) : segments.length
    const minLen = Math.min(segments.length, 3)
    for (let len = startLen; len >= minLen; len--) {
      const candidatePath = '/' + segments.slice(0, len).join('/')
      const candidateUrl = candidatePath + search
      // Reuse cached snapshot if we've already grabbed this parent.
      if (snapshotByUrl.has(candidateUrl)) {
        return { snapshotUrl: candidateUrl, body: snapshotByUrl.get(candidateUrl) }
      }
      try {
        const res = await originalFetch(candidateUrl, { method: 'GET' })
        if (!res.ok) continue
        const ct = res.headers.get('content-type') || ''
        if (!ct.includes('application/json')) continue
        const body = await res.json()
        return { snapshotUrl: candidateUrl, body }
      } catch { /* try next level up */ }
    }
    return null
  }
  // A collection URL ends with a known entity name (optionally after a nested
  // parent path like `/deals/:id/`). E.g. /api/crm/deals, /api/crm/deals/123/projects.
  const COLLECTION_NAME_RE = /\/(clients|contacts|leads|deals|projects|tickets|tasks|notes|activities|meetings|calls|emails)\/?(\?.*)?$/i
  const isCollectionUrl = (url: string) => /^\/api\//i.test(new URL(url, window.location.origin).pathname) && COLLECTION_NAME_RE.test(new URL(url, window.location.origin).pathname)
  const parentCollectionOf = (url: string): string => {
    try {
      const u = new URL(url, window.location.origin)
      u.pathname = u.pathname.replace(/\/[^/]+\/?$/, '')
      return u.pathname + u.search
    } catch { return url.replace(/\/[^/]+\/?(\?.*)?$/, '') }
  }
  // Parse the collection-base from a POST-create URL: strip query params and any
  // trailing slash. E.g. POST `/api/crm/contacts?foo=1` → `/api/crm/contacts`.
  const collectionBaseOf = (postUrl: string): string => {
    try {
      const u = new URL(postUrl, window.location.origin)
      return u.pathname.replace(/\/$/, '')
    } catch {
      return postUrl.split('?')[0].replace(/\/$/, '')
    }
  }
  // Last segment of the collection-base, e.g. `/api/crm/contacts` → `contacts`.
  const collectionNameOf = (base: string): string => {
    const parts = base.split('/').filter(Boolean)
    return parts[parts.length - 1] || ''
  }
  // Try a sequence of known BotPros delete/archive patterns until one succeeds.
  // Returns true on first 2xx; logs a warning and returns false if all fail.
  const tryDeletePatterns = async (base: string, id: string | number): Promise<boolean> => {
    const idStr = encodeURIComponent(String(id))
    const jsonHeaders = { 'Content-Type': 'application/json' } as Record<string, string>
    const collection = collectionNameOf(base)
    // BotPros entity-specific endpoints take priority — these are the canonical
    // archive routes the CRM admin UI itself uses, so they're the most reliable.
    const isClients = /\/api\/crm\/clients$/i.test(base) // contacts
    const isLeads = /\/api\/crm\/leads$/i.test(base)
    const isDeals = /\/api\/crm\/deals$/i.test(base)
    const isSubProjects = /\/api\/sub-admin\/projects$/i.test(base)
    const priorityAttempts: Array<{ url: string; init: RequestInit }> = []
    if (isClients) {
      // Canonical contact-archive endpoint (api/src/index.ts:4065)
      priorityAttempts.push({
        url: `/api/crm/clients/archive`,
        init: {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({ ids: [id], archive: true }),
        },
      })
    }
    if (isLeads) {
      priorityAttempts.push({
        url: `/api/crm/leads/batch/archive`,
        init: {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({ ids: [id], archive: true }),
        },
      })
    }
    if (isDeals) {
      priorityAttempts.push({
        url: `/api/crm/deals/batch/archive`,
        init: {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({ ids: [id], archive: true }),
        },
      })
    }
    if (isSubProjects) {
      priorityAttempts.push({
        url: `${base}/${idStr}`,
        init: { method: 'DELETE', headers: jsonHeaders },
      })
    }
    const attempts: Array<{ url: string; init: RequestInit }> = [
      ...priorityAttempts,
      // 1. RESTful default
      { url: `${base}/${idStr}`, init: { method: 'DELETE', headers: jsonHeaders } },
      // 2. Verb-style POST .../delete
      { url: `${base}/${idStr}/delete`, init: { method: 'POST', headers: jsonHeaders } },
      // 3. Soft delete POST .../archive
      { url: `${base}/${idStr}/archive`, init: { method: 'POST', headers: jsonHeaders } },
      // 4. Generic batch archive fallback (other CRM collections)
      ...(collection && !isClients && !isLeads && !isDeals ? [{
        url: `/api/crm/${collection}/batch/archive`,
        init: {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({ ids: [id], archive: true }),
        } as RequestInit,
      }] : []),
      // 6. Flag-style PATCH
      {
        url: `${base}/${idStr}`,
        init: {
          method: 'PATCH',
          headers: jsonHeaders,
          body: JSON.stringify({ deleted: true }),
        },
      },
      {
        url: `${base}/${idStr}`,
        init: {
          method: 'PATCH',
          headers: jsonHeaders,
          body: JSON.stringify({ archived: true }),
        },
      },
    ]
    for (const attempt of attempts) {
      try {
        const res = await originalFetch(attempt.url, attempt.init)
        if (res.ok) return true
      } catch { /* try next pattern */ }
    }
    console.warn(`[walkthrough] restoreWrites: no delete/archive pattern succeeded for url=${base} id=${id}; manual cleanup may be required`)
    return false
  }
  // BotPros APIs are mixed: GETs return snake_case, PATCHes expect camelCase
  // for some endpoints (e.g. /deals → expects stageId, not stage_id). To avoid
  // failed restores, we send a merged body with BOTH shapes. The endpoint picks
  // the keys it recognizes and ignores the rest.
  const dualCaseBody = (snake: Record<string, unknown>): Record<string, unknown> => {
    const out: Record<string, unknown> = { ...snake }
    for (const [k, v] of Object.entries(snake)) {
      if (!/_/.test(k)) continue
      const camel = k.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
      if (!(camel in out)) out[camel] = v
    }
    return out
  }
  // For sub-resource URLs (e.g. /api/sub-admin/projects/:id/owner) the parent
  // PATCH/PUT cannot restore — there's no bare PATCH /:id. Map the trailing
  // sub-resource segment to the snake_case keys we'd expect in the snapshot.
  const inferFieldFromSubresource = (subresourceName: string): string[] => {
    const name = subresourceName.toLowerCase()
    const map: Record<string, string[]> = {
      owner: ['owner_id', 'owner'],
      staff: ['staff_id', 'staff'],
      senior: ['senior_id', 'senior'],
      dates: ['start_date', 'end_date'],
      start: ['start_date', 'started_at'],
      pause: ['paused_at', 'pause_reason'],
    }
    if (map[name]) return map[name]
    // Generic fallback: <name> and <name>_id
    return [`${name}_id`, name]
  }
  const restoreWrites = async () => {
    let reversed = 0
    let failed = 0
    // Iterate in reverse order so dependent records unwind correctly.
    for (let k = writeLog.length - 1; k >= 0; k--) {
      const entry = writeLog[k]
      try {
        if (entry.method === 'POST' && entry.createdId !== undefined) {
          // Inverse of POST = delete/archive the new record. BotPros CRM has
          // several deletion patterns (RESTful DELETE, POST /delete, POST /archive,
          // batch archive, flag-style PATCH) — try each until one succeeds.
          const base = collectionBaseOf(entry.url)
          const ok = await tryDeletePatterns(base, entry.createdId)
          if (ok) reversed++; else failed++
        } else if ((entry.method === 'PUT' || entry.method === 'PATCH') && entry.originalBody !== undefined) {
          const target = entry.snapshotUrl || entry.url
          const snapshot = entry.originalBody as Record<string, unknown>
          const merged = dualCaseBody(snapshot)
          const body = JSON.stringify(merged)
          const headers = { 'Content-Type': 'application/json' }
          // BotPros uses PATCH for updates; try it first. Fall back to PUT.
          const patchRes = await originalFetch(target, { method: 'PATCH', headers, body, credentials: 'include' as RequestCredentials })
          console.info('[walkthrough] restore PATCH', target, '→', patchRes.status)
          if (patchRes.ok) {
            reversed++
          } else {
            const putRes = await originalFetch(target, { method: 'PUT', headers, body, credentials: 'include' as RequestCredentials })
            console.info('[walkthrough] restore PUT', target, '→', putRes.status)
            if (putRes.ok) {
              reversed++
            } else {
              // Fallback: many BotPros endpoints have NO bare PATCH /:id — they
              // only expose sub-resource URLs (/owner, /staff, /dates, /senior,
              // /start, /pause, etc). Detect a sub-resource original write URL
              // and replay PATCH against it with the relevant snapshot field.
              let subOk = false
              try {
                const origPath = new URL(entry.url, window.location.origin).pathname
                const apiIdx = origPath.indexOf('/api/')
                if (apiIdx >= 0) {
                  const tail = origPath.slice(apiIdx + 5).replace(/\/$/, '')
                  const segs = tail.split('/').filter(Boolean)
                  // 4+ segments after /api/ ⇒ sub-resource (e.g. sub-admin/projects/123/owner)
                  if (segs.length >= 4) {
                    const subName = segs[segs.length - 1]
                    const candidateKeys = inferFieldFromSubresource(subName)
                    // BotPros APIs often wrap the record in {success, data:{<entity>:{...}}}.
                    // Search the snapshot recursively for a key match (limited depth).
                    const lookup = (obj: any, key: string, depth = 0): unknown => {
                      if (!obj || typeof obj !== 'object' || depth > 4) return undefined
                      if (key in obj) return obj[key]
                      for (const v of Object.values(obj)) {
                        if (v && typeof v === 'object') {
                          const found = lookup(v, key, depth + 1)
                          if (found !== undefined) return found
                        }
                      }
                      return undefined
                    }
                    const subBody: Record<string, unknown> = {}
                    for (const key of candidateKeys) {
                      const val = lookup(snapshot, key)
                      if (val !== undefined) {
                        subBody[key] = val
                        const camel = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
                        if (camel !== key) subBody[camel] = val
                      }
                    }
                    if (Object.keys(subBody).length > 0) {
                      const subRes = await originalFetch(entry.url, {
                        method: 'PATCH',
                        headers,
                        body: JSON.stringify(subBody),
                        credentials: 'include' as RequestCredentials,
                      })
                      console.info('[walkthrough] restore PATCH', entry.url, '→', subRes.status)
                      if (subRes.ok) { reversed++; subOk = true }
                    } else {
                      console.warn('[walkthrough] sub-resource fallback: no snapshot key found for', subName, 'tried', candidateKeys)
                    }
                  }
                }
              } catch (e) {
                console.warn('[walkthrough] sub-resource fallback errored for', entry.url, e)
              }
              if (!subOk) { failed++; console.warn('[walkthrough] restore failed for', target, 'PATCH:', patchRes.status, 'PUT:', putRes.status) }
            }
          }
        } else if (entry.method === 'DELETE' && entry.originalBody !== undefined) {
          // Inverse of DELETE = POST snapshot back to parent collection.
          const parent = parentCollectionOf(entry.url)
          const res = await originalFetch(parent, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dualCaseBody(entry.originalBody as Record<string, unknown>)),
          })
          if (res.ok) reversed++; else failed++
        }
      } catch (err) {
        failed++
        console.warn('[walkthrough] restoreWrites failed for entry', entry, err)
      }
    }
    if (writeLog.length > 0) {
      console.info(`[walkthrough] auto-undo: ${reversed} writes reversed, ${failed} failed (see warnings above)`)
    }
  }
  // Walkthrough mode: synthesize success for ALL writes (no real backend hits)
  // so emails, SMS, side effects, and persisted rows never happen. This replaces
  // the older snapshot/rollback model — rollback couldn't reverse already-sent
  // notifications, which is exactly the failure the user is reporting.
  if (!tutorialCreationActive) window.fetch = ((input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    try {
      const method = (init?.method || (input instanceof Request ? input.method : 'GET') || 'GET').toUpperCase()
      let url: string
      if (typeof input === 'string') url = input
      else if (input instanceof URL) url = input.href
      else url = (input as Request).url
      if (blockedMethods.has(method) && writePathRe.test(url)) {
        console.info('[walkthrough] write blocked (synthetic 200):', method, url)
        const isCollectionCreate = method === 'POST' && isCollectionUrl(url) &&
          !/\/api\/[a-z-]+\/[a-z-]+\/[^/]+\/[a-z-]+\/?$/i.test(new URL(url, window.location.origin).pathname)
        // If this blocked create matches a declared ephemeral fixture, activate
        // it and return ITS id — so the just-"created" Johnny Test record shows
        // up (via GET splicing) and is openable at /record/<id>.
        const createFx = isCollectionCreate ? matchCreateFixture(url) : null
        if (createFx) activeFixtures.set(createFx.key, createFx)
        // Collection POSTs need a fake id-shaped body so the caller's success
        // handler (which often navigates to /record/<id>) doesn't crash on undefined.
        const fakeId = createFx
          ? createFx.id
          : ((typeof crypto !== 'undefined' && (crypto as any).randomUUID)
            ? (crypto as any).randomUUID()
            : `walkthrough-${Date.now()}`)
        const body = isCollectionCreate
          ? JSON.stringify({ success: true, walkthrough_blocked: true, id: fakeId, data: { id: fakeId, temp_password: 'walkthrough-demo' } })
          : JSON.stringify({ success: true, walkthrough_blocked: true })
        return Promise.resolve(new Response(body, { status: 200, headers: { 'Content-Type': 'application/json' } }))
      }
      // GET interception for active ephemeral fixtures: splice fake records into
      // list responses and serve the single-record GET so they're openable.
      if (method === 'GET' && activeFixtures.size > 0) {
        try {
          const u = new URL(url, window.location.origin)
          const recFx = recordFixtureForUrl(u.pathname)
          if (recFx) return Promise.resolve(jsonResponse(recFx.build()))
          // Sub-admin PROJECT-DETAIL for the Test Project fixture (tutorial 11
          // owner-approval tail). The embedded portal's detail GET
          // (/api/sub-admin/projects/TUT-test-project) 404s in the sandbox; serve
          // the pending-approval fixture so previewing AS the demo owner renders
          // the Approve button (data-tour-id="sub-project-approve"). Gated on the
          // Test Project fixture being active so it only applies in that tour.
          if (activeFixtures.has('test-project') && isTutSubProjectDetailUrl(u.pathname)) {
            return Promise.resolve(jsonResponse({ success: true, data: buildTestSubProjectDetail() }))
          }
          // Unified search (GET /api/crm/search/unified?q=…) powers the Create-New
          // / Smart-Create contact-suggestion dropdown — a DIFFERENT endpoint than
          // the contacts LIST. Splice active fixtures into its contacts/leads
          // arrays too so typing "Test Deal" surfaces the mock in the dropdown.
          if (/\/api\/crm\/search\/unified\/?$/i.test(u.pathname)) {
            const q = u.searchParams.get('q') || ''
            return originalFetch(input as any, init).then(async real => {
              try {
                const ct = real.headers.get('content-type') || ''
                if (!real.ok || !ct.includes('application/json')) return real
                const data: any = await real.clone().json()
                const added = spliceFixturesIntoUnified(data, Array.from(activeFixtures.values()), q)
                return added ? jsonResponse(data) : real
              } catch {
                return real
              }
            })
          }
          const kind = listKindForUrl(u.pathname)
          if (kind) {
            const search = u.searchParams.get('search') || ''
            return originalFetch(input as any, init).then(real => spliceListResponse(real, kind, search))
          }
        } catch { /* fall through */ }
      }
      // Legacy snapshot/track path retained but unreachable — kept to preserve
      // closures (writeLog, snapshotByUrl, etc.) used by done()/exit() cleanup.
      if (false && blockedMethods.has(method) && writePathRe.test(url)) {
        // Snapshot-then-pass-through. Returns a Promise<Response>. Track the
        // promise in inFlightWrites so done()/exit() can wait for any pending
        // creates BEFORE running cleanup — otherwise auto-undo runs with an
        // empty createdRecords map and the new record persists.
        const promise = (async (): Promise<Response> => {
          let snapInfo: { snapshotUrl: string; body: any } | null = null
          // For ANY mutating method we want a parent-record snapshot. For nested
          // collection POSTs (e.g. POST /deals/:id/projects), the create has
          // side effects on the parent deal (stage changes, etc) — so we BOTH
          // snapshot the parent (deal) AND later track the createdId (project).
          const isNestedCollectionPost = method === 'POST' && isCollectionUrl(url) && /\/api\/[a-z-]+\/[a-z-]+\/[^/]+\/[a-z-]+\/?$/i.test(new URL(url, window.location.origin).pathname)
          if (method === 'PUT' || method === 'PATCH' || method === 'DELETE' ||
              (method === 'POST' && !isCollectionUrl(url)) ||
              isNestedCollectionPost) {
            try {
              snapInfo = await findSnapshot(url, { skipExact: isNestedCollectionPost })
              if (!snapInfo) {
                console.warn('[walkthrough] no JSON snapshot found at any parent of', url, '— write will not be auto-reverted')
              } else if (!snapshotByUrl.has(snapInfo.snapshotUrl)) {
                snapshotByUrl.set(snapInfo.snapshotUrl, snapInfo.body)
              }
            } catch (e) {
              console.warn('[walkthrough] snapshot lookup errored for', url, e)
            }
          }
          const realRes = await originalFetch(input as any, init)
          // Track the write (only on success-ish responses).
          console.info('[walkthrough] write', method, url, '→', realRes.status, 'ok:', realRes.ok)
          if (realRes.ok) {
            if (method === 'POST' && isCollectionUrl(url)) {
              try {
                const peek = await realRes.clone().json()
                console.info('[walkthrough] POST collection response peek:', peek)
                // BotPros APIs return varied shapes — contacts return the full record
                // with `unified_client_id` (no `.id`), batch endpoints wrap in `.data`,
                // some routes nest under entity keys (`.contact`, `.deal`, etc).
                // Walk a prioritized list and fall back to any *_id / id field on the body.
                const pickId = (b: any): string | number | undefined => {
                  if (!b || typeof b !== 'object') return undefined
                  const direct =
                    b.id ??
                    b.unified_client_id ??
                    b.data?.id ??
                    b.data?.unified_client_id ??
                    b.record?.id ??
                    b.record?.unified_client_id ??
                    b.contact?.id ??
                    b.contact?.unified_client_id ??
                    b.deal?.id ??
                    b.lead?.id ??
                    b.project?.id ??
                    b.ticket?.id
                  if (direct !== undefined && direct !== null) return direct
                  // Generic fallback: any string-or-number field whose key === 'id'
                  // or ends in '_id'.
                  for (const [k, v] of Object.entries(b)) {
                    if ((k === 'id' || /_id$/.test(k)) && (typeof v === 'string' || typeof v === 'number') && v !== '') {
                      return v
                    }
                  }
                  return undefined
                }
                const createdId = pickId(peek)
                console.info('[walkthrough] extracted id:', createdId, 'from', url)
                if (createdId !== undefined && createdId !== null) {
                  writeLog.push({ method, url, createdId, body: peek })
                  // Track for final hard-delete cleanup pass.
                  const key = classifyCreatedUrl(url)
                  if (key) {
                    if (!createdRecords[key]) createdRecords[key] = []
                    createdRecords[key].push(createdId)
                    // For projects, the URL ambiguity between /api/crm/projects vs
                    // /api/sub-admin/projects means we should also try the OTHER
                    // table — server-side missing-id deletes are no-ops.
                    if (key === 'crm_projects') {
                      if (!createdRecords['sub_projects']) createdRecords['sub_projects'] = []
                      createdRecords['sub_projects'].push(createdId)
                    } else if (key === 'sub_projects') {
                      if (!createdRecords['crm_projects']) createdRecords['crm_projects'] = []
                      createdRecords['crm_projects'].push(createdId)
                    }
                  }
                }
                // For nested-collection POSTs (e.g. /deals/:id/projects), the
                // parent record (deal) often has side-effect changes too. Also
                // push a parent-snapshot replay entry so we can revert those.
                if (snapInfo) {
                  const already = writeLog.some(e =>
                    (e.method === 'PUT' || e.method === 'PATCH') &&
                    e.snapshotUrl === snapInfo!.snapshotUrl
                  )
                  if (!already) {
                    writeLog.push({ method: 'PUT', url, snapshotUrl: snapInfo.snapshotUrl, originalBody: snapInfo.body })
                  }
                }
              } catch { /* non-JSON POST response — skip tracking */ }
            } else if ((method === 'PUT' || method === 'PATCH' || method === 'POST') && snapInfo) {
              // Only track once per parent snapshotUrl — single restore PUT covers all fields.
              const already = writeLog.some(e =>
                (e.method === 'PUT' || e.method === 'PATCH' || e.method === 'POST') &&
                e.snapshotUrl === snapInfo!.snapshotUrl
              )
              if (!already) {
                writeLog.push({ method: 'PUT', url, snapshotUrl: snapInfo.snapshotUrl, originalBody: snapInfo.body })
              }
            } else if (method === 'DELETE' && snapInfo) {
              writeLog.push({ method, url, snapshotUrl: snapInfo.snapshotUrl, originalBody: snapInfo.body })
            }
          }
          return realRes
        })()
        inFlightWrites.add(promise)
        promise.finally(() => inFlightWrites.delete(promise))
        return promise
      }
    } catch { /* fall through */ }
    return originalFetch(input, init)
  }) as typeof window.fetch

  const top = document.createElement('div'); top.className = 'kbwalk-backdrop-piece'
  const right = document.createElement('div'); right.className = 'kbwalk-backdrop-piece'
  const bottom = document.createElement('div'); bottom.className = 'kbwalk-backdrop-piece'
  const left = document.createElement('div'); left.className = 'kbwalk-backdrop-piece'
  root.append(top, right, bottom, left)

  const ring = document.createElement('div')
  ring.className = 'kbwalk-ring'
  ring.style.display = 'none'
  root.appendChild(ring)

  const popover = document.createElement('div')
  popover.className = 'kbwalk-popover'
  root.appendChild(popover)

  // Live-mode banner: subtle indicator that writes will be auto-reverted on exit.
  // Skip when Tutorial Creation Mode is on — that mode blocks writes outright,
  // so there's nothing to revert and the banner would be misleading.
  let liveBanner: HTMLDivElement | null = null
  if (!tutorialCreationActive) {
    liveBanner = document.createElement('div')
    liveBanner.className = 'kbwalk-live-banner'
    liveBanner.textContent = '🔵 Live preview — nothing you do here is saved'
    Object.assign(liveBanner.style, {
      position: 'fixed',
      bottom: '16px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'rgba(37, 99, 235, 0.92)',
      color: '#fff',
      padding: '6px 14px',
      borderRadius: '999px',
      fontSize: '12px',
      fontWeight: '500',
      zIndex: '2147483647',
      boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
      pointerEvents: 'none',
    } as CSSStyleDeclaration)
    root.appendChild(liveBanner)
  }

  const prevScrollBehavior = document.documentElement.style.scrollBehavior

  let cleanupCurrentStep: () => void = () => {}
  // Allows in-flight resolveElementWithObserver waits to be cancelled when the
  // user backs/advances/exits before the element appears.
  let cancelPendingResolve: () => void = () => {}

  const blockTab = (e: KeyboardEvent) => {
    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
    }
  }
  document.addEventListener('keydown', blockTab, true)

  const cleanup = () => {
    try { cancelPendingResolve() } catch { /* */ }
    cancelPendingResolve = () => {}
    try { stopRefocusInterval() } catch { /* */ }
    try { stopRepositionTracking() } catch { /* */ }
    try { cleanupCurrentStep() } catch { /* */ }
    cleanupCurrentStep = () => {}
    if (!tutorialCreationActive) { try { window.fetch = originalFetch } catch { /* */ } }
    // Drop all ephemeral fixtures so no fake record can survive teardown. The
    // fetch shim is already restored above, so subsequent GETs hit real data;
    // exit()/done() additionally full-reload to flush any React-held copies.
    try { activeFixtures.clear() } catch { /* */ }
    // Restore the Create-drafts blob to its pre-walkthrough state: drop any
    // tutorial-created draft, preserve the user's real drafts. Absent-at-start ⇒
    // remove the key entirely.
    try {
      if (createDraftsSnapshot === null) window.localStorage.removeItem(CREATE_DRAFTS_KEY)
      else window.localStorage.setItem(CREATE_DRAFTS_KEY, createDraftsSnapshot)
    } catch { /* */ }
    try { (window as any).__kbWalkthroughActive = false; window.dispatchEvent(new CustomEvent('kb-walkthrough-changed')) } catch { /* */ }
    if (root.parentNode) root.parentNode.removeChild(root)
    document.documentElement.style.scrollBehavior = prevScrollBehavior
    document.removeEventListener('keydown', onKey)
    document.removeEventListener('keydown', blockTab, true)
    document.removeEventListener('click', fileInputBlocker, true)
    window.removeEventListener('resize', reposition)
    window.removeEventListener('scroll', reposition, true)
  }

  // While a walkthrough is running, intercept clicks that would either:
  //  (a) pop the OS file picker (native <input type=file> or its wrapping
  //      <label>) — that dialog is a modal we can't auto-undo, or
  //  (b) commit a real write that the auto-undo pipeline doesn't cover
  //      (currently: the profile About-tab Save button).
  // Treat both as exit signals: swallow the click and end the tutorial.
  const fileInputBlocker = (e: MouseEvent) => {
    if (cancelled) return
    const target = e.target
    if (!(target instanceof Element)) return
    if (target.closest('.kbwalk-popover') || target.closest('.kbwalk-ring') || target.closest('.kbwalk-backdrop-piece')) return
    const direct = target.closest('input[type="file"]') as HTMLInputElement | null
    const viaLabel = (target.closest('label') as HTMLLabelElement | null)?.querySelector('input[type="file"]') as HTMLInputElement | null
    const profileSave = target.closest('.profile-edit-foot .m365-btn-primary, .profile-edit-foot button[type="submit"]')
    if (!direct && !viaLabel && !profileSave) return
    e.preventDefault()
    e.stopPropagation()
    try { (e as any).stopImmediatePropagation?.() } catch { /* */ }
    exit()
  }
  document.addEventListener('click', fileInputBlocker, true)

  const showCleanupOverlay = () => {
    try {
      const o = document.createElement('div')
      o.id = 'kb-walk-cleanup-overlay'
      o.style.cssText = 'position:fixed;inset:0;z-index:2147483646;background:#0f172a;color:#fff;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:14px;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;'
      o.innerHTML = '<div style="width:36px;height:36px;border:3px solid rgba(255,255,255,0.2);border-top-color:#fff;border-radius:50%;animation:kbwalk-spin 1s linear infinite"></div><div style="font-size:14px;font-weight:500">Cleaning up walkthrough…</div><style>@keyframes kbwalk-spin{to{transform:rotate(360deg)}}</style>'
      document.body.appendChild(o)
    } catch { /* */ }
  }
  const exit = () => {
    if (cancelled) return
    cancelled = true
    showCleanupOverlay()
    console.info('[walkthrough] exit() — waiting for in-flight writes:', inFlightWrites.size)
    waitForWrites().then(() => {
      console.info('[walkthrough] running auto-undo. createdRecords:', JSON.stringify(createdRecords))
      return restoreWrites()
    }).catch(err => console.warn('[walkthrough] restoreWrites threw', err))
      .then(() => hardCleanup())
      .finally(() => {
        cleanup()
        try { (window as any).__resetSubPortalPreview?.() } catch { /* */ }
        // Hard reload after auto-undo so all dashboards/lists refetch fresh
        // data and stale React state from the walkthrough run is cleared.
        try { window.location.href = window.location.origin + '/#/' } catch { try { opts.navigate('/') } catch { /* */ } }
        try { window.location.reload() } catch { /* */ }
        opts.onExit?.()
      })
  }

  const done = () => {
    if (cancelled) return
    cancelled = true
    showCleanupOverlay()
    console.info('[walkthrough] done() — waiting for in-flight writes:', inFlightWrites.size)
    waitForWrites().then(() => {
      console.info('[walkthrough] running auto-undo. createdRecords:', JSON.stringify(createdRecords))
      return restoreWrites()
    }).catch(err => console.warn('[walkthrough] restoreWrites threw', err))
      .then(() => hardCleanup())
      .finally(() => {
        cleanup()
        try { (window as any).__resetSubPortalPreview?.() } catch { /* */ }
        // Hard reload after auto-undo so all dashboards/lists refetch fresh
        // data and stale React state from the walkthrough run is cleared.
        try { window.location.href = window.location.origin + '/#/' } catch { try { opts.navigate('/') } catch { /* */ } }
        try { window.location.reload() } catch { /* */ }
        opts.onDone?.()
      })
  }

  const onKey = (e: KeyboardEvent) => {
    if (cancelled) return
    if (e.key === 'Escape') {
      if (synthEscapeIgnore) return
      e.preventDefault(); exit()
    }
  }
  document.addEventListener('keydown', onKey)

  // Currently anchored rect (or null = centered)
  let currentRect: DOMRect | null = null
  let currentBorderRadius = '8px'

  const layoutBackdrop = (rect: DOMRect | null, opts?: { hideBottom?: boolean }) => {
    const vw = window.innerWidth
    const vh = window.innerHeight
    if (!rect) {
      // full overlay via "top" piece, hide others
      top.style.top = '0px'; top.style.left = '0px'; top.style.width = vw + 'px'; top.style.height = vh + 'px'
      right.style.display = 'none'; bottom.style.display = 'none'; left.style.display = 'none'
      return
    }
    right.style.display = ''; bottom.style.display = opts?.hideBottom ? 'none' : ''; left.style.display = ''
    top.style.top = '0px'; top.style.left = '0px'; top.style.width = vw + 'px'; top.style.height = Math.max(0, rect.top) + 'px'
    bottom.style.top = (rect.bottom) + 'px'; bottom.style.left = '0px'; bottom.style.width = vw + 'px'; bottom.style.height = Math.max(0, vh - rect.bottom) + 'px'
    left.style.top = rect.top + 'px'; left.style.left = '0px'; left.style.width = Math.max(0, rect.left) + 'px'; left.style.height = rect.height + 'px'
    right.style.top = rect.top + 'px'; right.style.left = rect.right + 'px'; right.style.width = Math.max(0, vw - rect.right) + 'px'; right.style.height = rect.height + 'px'
  }

  const layoutRing = (rect: DOMRect | null) => {
    if (!rect) { ring.style.display = 'none'; return }
    ring.style.display = ''
    const pad = 4
    ring.style.top = (rect.top - pad) + 'px'
    ring.style.left = (rect.left - pad) + 'px'
    ring.style.width = (rect.width + pad * 2) + 'px'
    ring.style.height = (rect.height + pad * 2) + 'px'
    ring.style.borderRadius = currentBorderRadius
  }

  const layoutPopover = (rect: DOMRect | null) => {
    const vw = window.innerWidth
    const vh = window.innerHeight
    const pw = popover.offsetWidth || 320
    const ph = popover.offsetHeight || 160
    const gap = 16
    let top_ = 0, left_ = 0
    if (!rect) {
      top_ = (vh - ph) / 2
      left_ = (vw - pw) / 2
    } else {
      const spaceBelow = vh - rect.bottom
      const spaceAbove = rect.top
      const spaceRight = vw - rect.right
      const spaceLeft = rect.left
      // For SELECT elements (and other inputs that open a downward dropdown
      // overlapping the popover), prefer above → right → left → below.
      const isDropdownEl = currentEl?.tagName === 'SELECT' ||
        (currentEl?.tagName === 'INPUT' && (currentEl as HTMLInputElement).getAttribute('list') !== null)
      if (isDropdownEl) {
        if (spaceAbove >= ph + gap) {
          top_ = rect.top - ph - gap
          left_ = rect.left + rect.width / 2 - pw / 2
        } else if (spaceRight >= pw + gap) {
          left_ = rect.right + gap
          top_ = rect.top + rect.height / 2 - ph / 2
        } else if (spaceLeft >= pw + gap) {
          left_ = rect.left - pw - gap
          top_ = rect.top + rect.height / 2 - ph / 2
        } else if (spaceBelow >= 200) {
          top_ = rect.bottom + gap
          left_ = rect.left + rect.width / 2 - pw / 2
        } else {
          top_ = (vh - ph) / 2
          left_ = (vw - pw) / 2
        }
      } else if (spaceBelow >= 200) {
        top_ = rect.bottom + gap
        left_ = rect.left + rect.width / 2 - pw / 2
      } else if (spaceAbove >= ph + gap) {
        top_ = rect.top - ph - gap
        left_ = rect.left + rect.width / 2 - pw / 2
      } else if (spaceRight >= pw + gap) {
        left_ = rect.right + gap
        top_ = rect.top + rect.height / 2 - ph / 2
      } else if (spaceLeft >= pw + gap) {
        left_ = rect.left - pw - gap
        top_ = rect.top + rect.height / 2 - ph / 2
      } else {
        top_ = (vh - ph) / 2
        left_ = (vw - pw) / 2
      }
    }
    // Clamp to viewport
    left_ = Math.max(12, Math.min(vw - pw - 12, left_))
    top_ = Math.max(12, Math.min(vh - ph - 12, top_))
    popover.style.top = top_ + 'px'
    popover.style.left = left_ + 'px'
  }

  const reposition = () => {
    // Recompute rect for current element if any
    if (currentEl) {
      const r = currentEl.getBoundingClientRect()
      currentRect = r
      layoutBackdrop(r)
      layoutRing(r)
      layoutPopover(r)
    } else {
      layoutBackdrop(currentRect)
      layoutRing(currentRect)
      layoutPopover(currentRect)
    }
  }
  window.addEventListener('resize', reposition)
  window.addEventListener('scroll', reposition, true)

  // ── Live target tracking through post-load reflow ──────────────────────────
  // window 'resize'/'scroll' alone DON'T fire when async content finishes
  // loading and reflows the page. On the contact-record (360) page the Open
  // Deals / Active Projects / Lifetime Won / Open Tickets / Leads / Projects
  // panels + AI summary resolve AFTER a step is positioned and push the target
  // (e.g. the "+ add deal" icon, step 5/6 `[data-tour-id="deal-360-add"]`) down
  // — the spotlight, measured once, stays at the stale rect and lands over the
  // empty "Leads (0)" area. These observers keep the spotlight + tooltip glued
  // to the target's CURRENT rect until the step advances. Fully general: every
  // step gets re-tracked, so already-correct steps (e.g. the create-deal "+")
  // are unaffected. All torn down on step change + teardown (no leaks).
  let trackResizeObs: ResizeObserver | null = null
  let trackMutationObs: MutationObserver | null = null
  let trackRafId: number | null = null
  let lastTrackedKey = ''
  const stopRepositionTracking = () => {
    if (trackResizeObs) { try { trackResizeObs.disconnect() } catch { /* */ } trackResizeObs = null }
    if (trackMutationObs) { try { trackMutationObs.disconnect() } catch { /* */ } trackMutationObs = null }
    if (trackRafId !== null) { try { cancelAnimationFrame(trackRafId) } catch { /* */ } trackRafId = null }
    lastTrackedKey = ''
  }
  const startRepositionTracking = (el: HTMLElement) => {
    stopRepositionTracking()
    lastTrackedKey = rectKey(el.getBoundingClientRect())
    // ResizeObserver: fires when the target OR the document body changes size
    // (async content makes the body taller / the element itself resizes).
    try {
      trackResizeObs = new ResizeObserver(() => { if (!cancelled && currentEl) reposition() })
      trackResizeObs.observe(el)
      try { trackResizeObs.observe(document.body) } catch { /* */ }
    } catch { /* ResizeObserver unsupported */ }
    // MutationObserver: async panels inserting/removing nodes shift layout
    // WITHOUT a resize or scroll. Re-measure on any body subtree mutation —
    // but ignore mutations originating inside our own overlay (writing the
    // backdrop/ring/popover styles would otherwise feed back and loop).
    try {
      trackMutationObs = new MutationObserver(muts => {
        if (cancelled || !currentEl) return
        let external = false
        for (const m of muts) { if (m.target && !root.contains(m.target)) { external = true; break } }
        if (external) reposition()
      })
      trackMutationObs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] })
    } catch { /* */ }
    // rAF settle window: a short loop catches reflows the observers can miss
    // (font swaps, image decode) for ~2.5s after resolve; it only relayouts when
    // the target's rect ACTUALLY changed, then stops once the window elapses.
    const settleStart = Date.now()
    const tick = () => {
      if (cancelled || !currentEl) { trackRafId = null; return }
      const cur = currentEl.getBoundingClientRect()
      if (hasRectMoved(lastTrackedKey, cur)) { lastTrackedKey = rectKey(cur); reposition() }
      if (Date.now() - settleStart < 2500) trackRafId = requestAnimationFrame(tick)
      else trackRafId = null
    }
    trackRafId = requestAnimationFrame(tick)
  }

  let currentEl: HTMLElement | null = null
  // When advancing FROM a Type step, capture the input element so we can keep
  // refocusing it during the next step's element-resolution window. This keeps
  // autocomplete dropdowns (e.g. Google Maps) visible long enough for the next
  // step (a click on a suggestion) to resolve its element.
  let pendingFocusEl: HTMLElement | null = null
  let pendingFocusKind: ActionKind | null = null
  let refocusInterval: number | null = null
  const stopRefocusInterval = () => {
    if (refocusInterval !== null) {
      try { clearInterval(refocusInterval) } catch { /* */ }
      refocusInterval = null
    }
  }
  // Set by Next-button handler when it synthetically performs the step's action,
  // so the auto-advance click/blur listener installed on the same element doesn't
  // also fire and double-advance.
  let markAdvancedForCurrentStep: () => void = () => {}
  // Ignore-input window: after back()/advance(), in-flight events from the prior
  // step's button click can bleed into the next step's listener. Drop any events
  // whose timestamp falls before this deadline.
  let ignoringInputUntil = 0
  // Reserved: previously used to suppress a synthetic Escape keydown emitted
  // when closing a Create panel after blocking a destructive button. Live
  // mode no longer blocks anything, but the variable is still referenced by
  // the keydown handler below — keeping it as a permanent `false` is the
  // safest no-op.
  const synthEscapeIgnore = false

  const renderPopoverContent = (step: KbStep, foundEl: boolean) => {
    const { label, kind } = deriveAction(step)
    const total = opts.steps.length - consumed
    const visibleIndex = i - consumed
    const isFirst = i === consumed
    const note = foundEl ? '' : `<em>Couldn't auto-locate this element. Perform this step manually to continue.</em>`
    const extracted = extractExpectedValue(step) ?? ''
    const displayText = (kind === 'type' && extracted && !step.text)
      ? `${label} "${extracted}"`
      : step.text
    popover.innerHTML = `
      <button class="kbwalk-popover__close" data-act="close" aria-label="Exit walkthrough">&times;</button>
      <span class="kbwalk-badge kbwalk-badge--${kind}">${escapeHtml(label)}</span>
      <div class="kbwalk-step-counter">Step ${visibleIndex + 1} of ${total}</div>
      <div class="kbwalk-text">${escapeHtml(displayText)}${note}</div>
    `
    // Prevent popover buttons from stealing focus from the underlying input
    // (e.g. address field with Google Maps autocomplete). Without this, clicking
    // Prevent focus theft from underlying input AND prevent click from bubbling
    // past popover so React outside-click detectors (e.g. address autocomplete)
    // don't close their dropdowns. stopPropagation on mousedown/click at the
    // popover root catches all interior clicks without breaking target handlers.
    // GENERAL DEAD-END GUARD: every step must have a way forward. click/type
    // steps auto-advance when the user performs the action (forcing function
    // preserved); 'set'/'navigate'/'other' — and ANY kind whose element we
    // couldn't resolve — get a manual Continue button so no step can stall.
    // Built via DOM (not innerHTML) with static text — no untrusted markup.
    if (shouldAllowManualAdvance(kind, foundEl)) {
      const actions = document.createElement('div')
      actions.className = 'kbwalk-actions'
      const btn = document.createElement('button')
      btn.className = 'kbwalk-btn kbwalk-btn--primary'
      btn.setAttribute('data-act', 'continue')
      btn.textContent = i >= opts.steps.length - 1 ? 'Finish' : 'Continue'
      btn.addEventListener('click', () => {
        try { cleanupCurrentStep() } catch { /* */ }
        cleanupCurrentStep = () => {}
        advance()
      })
      actions.appendChild(btn)
      popover.appendChild(actions)
    }
    const preventFocusSteal = (e: MouseEvent) => { e.preventDefault() }
    popover.querySelectorAll<HTMLButtonElement>('.kbwalk-btn, .kbwalk-popover__close').forEach(btn => {
      btn.addEventListener('mousedown', preventFocusSteal, true)
    })
    const stopBubble = (e: Event) => e.stopPropagation()
    popover.addEventListener('mousedown', stopBubble)
    popover.addEventListener('click', stopBubble)
    popover.querySelector<HTMLButtonElement>('[data-act="close"]')?.addEventListener('click', exit)
  }

  const fadeOut = (): Promise<void> => new Promise(resolve => {
    popover.classList.remove('kbwalk-popover--visible')
    setTimeout(resolve, 250)
  })
  const fadeIn = () => {
    requestAnimationFrame(() => popover.classList.add('kbwalk-popover--visible'))
  }

  const applyAutofillSuppression = (el: HTMLElement): (() => void) => {
    const tag = (el.tagName || '').toUpperCase()
    if (tag !== 'INPUT' && tag !== 'TEXTAREA') return () => {}
    const attrs = ['autocomplete', 'data-bwignore', 'data-1p-ignore', 'data-lpignore', 'data-form-type', 'name']
    const original: Record<string, string | null> = {}
    for (const a of attrs) {
      original[a] = el.hasAttribute(a) ? el.getAttribute(a) : null
    }
    try {
      el.setAttribute('autocomplete', 'off')
      el.setAttribute('data-bwignore', 'true')
      el.setAttribute('data-1p-ignore', 'true')
      el.setAttribute('data-lpignore', 'true')
      el.setAttribute('data-form-type', 'other')
      const existingName = el.getAttribute('name')
      if (!existingName) {
        const rand = Math.random().toString(36).slice(2, 10)
        el.setAttribute('name', `kbwalk-noautofill-${rand}`)
      }
    } catch { /* */ }

    return () => {
      for (const a of attrs) {
        const orig = original[a]
        try {
          if (orig === null) el.removeAttribute(a)
          else el.setAttribute(a, orig)
        } catch { /* */ }
      }
    }
  }

  const attachAutoAdvance = (step: KbStep, el: HTMLElement) => {
    const { kind } = deriveAction(step)
    // Type steps have no auto-advance — user clicks the Next button instead.
    // Suppress autofill on Type fields but install no listener.
    if (kind === 'type') {
      const restoreAutofill = applyAutofillSuppression(el)
      let typeAdvanced = false
      let typeDebounce: number | null = null
      const clearTypeDebounce = () => {
        if (typeDebounce !== null) { try { clearTimeout(typeDebounce) } catch { /* */ } typeDebounce = null }
      }
      const expectedRaw = extractExpectedValue(step) ?? ''
      // Normalize whitespace so `"Johnny  Test"` (double space) matches user's
      // `Johnny Test` (single space), and tabs / line breaks don't trip us up.
      const normalize = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim()
      const expected = normalize(expectedRaw)
      let inputHandler: ((e: Event) => void) | null = null
      if (expected) {
        inputHandler = (e: Event) => {
          if (typeAdvanced || cancelled) return
          if (!(e as any).isTrusted) return
          if (Date.now() < ignoringInputUntil) return
          const val = (el as HTMLInputElement | HTMLTextAreaElement).value ?? ''
          const actual = normalize(String(val))
          if (actual.length > 0 && actual.includes(expected)) {
            clearTypeDebounce()
            // 600ms gives downstream search/filter UIs time to debounce + render
            // before the next step's resolver starts hunting.
            typeDebounce = window.setTimeout(() => {
              if (typeAdvanced || cancelled) return
              typeAdvanced = true
              try { ring.classList.add('kbwalk-ring--success') } catch { /* */ }
              setTimeout(() => { try { ring.classList.remove('kbwalk-ring--success') } catch { /* */ } }, 200)
              try { cleanupCurrentStep() } catch { /* */ }
              cleanupCurrentStep = () => {}
              advance()
            }, 600)
          } else {
            clearTypeDebounce()
          }
        }
        el.addEventListener('input', inputHandler, true)
      }
      cleanupCurrentStep = () => {
        try { restoreAutofill() } catch { /* */ }
        clearTypeDebounce()
        if (inputHandler) { try { el.removeEventListener('input', inputHandler, true) } catch { /* */ } }
      }
      // Type→ClickSuggestion fast-forward: if the next step is a Click with a
      // selector/xpath, watch for a doc-level click matching that next target.
      // The AddressInput component closes its suggestion list on any outside
      // mousedown — clicking our popover would dismiss it. Instead we let the
      // user click the suggestion directly, and when they do, we advance past
      // BOTH this Type step AND the next Click step in one motion.
      const stepsList = opts.steps
      const nextStep = stepsList[i + 1]
      if (nextStep && deriveAction(nextStep).kind === 'click' && (nextStep.selector || nextStep.xpath || /(suggestion|result|option|found in|from\s+list|dropdown|autocomplete)/i.test(nextStep.text || ''))) {
        const isSuggestionPick = /(suggestion|result|option|found in|from\s+list|dropdown|autocomplete)/i.test(nextStep.text || '')
        const wrapper = isSuggestionPick ? findInputWrapper(el) : null
        let advanced = false
        const nextHandler = (e: Event) => {
          if (advanced || cancelled) return
          const target = e.target
          if (!(target instanceof Element)) return
          if (target.closest('.kbwalk-popover') || target.closest('.kbwalk-ring')) return
          let matched = false
          if (nextStep.selector) {
            try { if (target.closest(nextStep.selector)) matched = true } catch { /* */ }
          }
          if (!matched && nextStep.xpath) {
            try {
              const r = document.evaluate(nextStep.xpath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null)
              const nx = r.singleNodeValue as Element | null
              if (nx && (target === nx || nx.contains(target))) matched = true
            } catch { /* */ }
          }
          if (!matched && wrapper && wrapper.contains(target) && target !== el && (target as HTMLElement).offsetWidth > 0 && (target as HTMLElement).offsetHeight > 0 && (target.textContent || '').trim().length > 0) {
            matched = true
          }
          if (!matched) return
          advanced = true
          // Fast-forward: skip the next click-target step too (user just did it).
          i += 1
          setTimeout(() => advance(), 80)
        }
        document.addEventListener('click', nextHandler, true)
        const prevCleanup = cleanupCurrentStep
        cleanupCurrentStep = () => {
          try { prevCleanup() } catch { /* */ }
          document.removeEventListener('click', nextHandler, true)
        }
      }
      return
    }
    // FIELD-SET steps ("Set/Update/Change/Pick/Toggle the … field"): advance when
    // the user actually commits a value into the step's target field. The target
    // (e.g. [data-tour-id="project-start-date"]) is a WRAPPER; its real <input>
    // renders on click-to-edit and its input/change bubbles — so we listen at
    // capture on document and match target.closest/contains against the resolved
    // element (or the stable tour-id hook). No expected_value ⇒ any change wins.
    if (kind === 'set') {
      const expectedRaw = extractExpectedValue(step) ?? ''
      let setAdvanced = false
      markAdvancedForCurrentStep = () => { setAdvanced = true }
      const setHandler = (e: Event) => {
        if (setAdvanced || cancelled) return
        const target = e.target
        if (!(target instanceof Element)) return
        let inTarget = false
        try { inTarget = !!(el && (target === el || el.contains(target) || target.contains(el))) } catch { /* */ }
        if (!inTarget && isTourIdSelector(step.selector)) {
          try { inTarget = !!target.closest(step.selector as string) } catch { /* */ }
        }
        const value = (target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value ?? ''
        const ok = fieldSetShouldAdvance({
          isTrusted: !!(e as any).isTrusted,
          withinIgnoreWindow: Date.now() < ignoringInputUntil,
          targetInsideStepTarget: inTarget,
          currentValue: String(value),
          expectedValue: expectedRaw,
        })
        if (!ok) return
        setAdvanced = true
        try { ring.classList.add('kbwalk-ring--success') } catch { /* */ }
        setTimeout(() => { try { ring.classList.remove('kbwalk-ring--success') } catch { /* */ } }, 200)
        try { cleanupCurrentStep() } catch { /* */ }
        cleanupCurrentStep = () => {}
        // Small delay so the field's onBlur/commit + React state settle before the
        // next step's resolver starts hunting.
        setTimeout(() => advance(), 120)
      }
      document.addEventListener('input', setHandler, true)
      document.addEventListener('change', setHandler, true)
      cleanupCurrentStep = () => {
        document.removeEventListener('input', setHandler, true)
        document.removeEventListener('change', setHandler, true)
      }
      return
    }
    if (kind !== 'click') return
    let advanced = false
    markAdvancedForCurrentStep = () => { advanced = true }
    const trigger = () => {
      if (advanced || cancelled) return
      advanced = true
      try { ring.classList.add('kbwalk-ring--success') } catch { /* */ }
      setTimeout(() => { try { ring.classList.remove('kbwalk-ring--success') } catch { /* */ } }, 200)
      try { cleanupCurrentStep() } catch { /* */ }
      cleanupCurrentStep = () => {}
      advance()
    }
    const targetEl = el

    // SELECTION-AWARE advance (custom DIV-checkbox rows — e.g. the project-owner
    // option row). For a "Select "Johnny Test" as the owner" step the resolver
    // returns the owner-NAME leaf span, but the clickable control is its ANCESTOR
    // row (which carries the `onClick` toggle + `data-selected`/`aria-checked`).
    // A user naturally clicks the checkbox / row body (not the exact name span),
    // which toggles selection but is MISSED by the plain element-match + text
    // fallback below → the step dead-ended (bug 2026-07-01). Fix, scoped strictly
    // to THIS step's target:
    //  (a) PRIMARY — observe the selectable row's selection attributes and advance
    //      when they flip truthy (works no matter where in the row the click lands
    //      and even if selection is set programmatically), and
    //  (b) FALLBACK — broaden the click-match (below) to the whole row.
    // Native <select> steps are excluded (they have no data-selected/aria-checked
    // and advance via the `change` handler above); non-selection steps find no
    // such row (selectRow === el) and get an observer that never fires — no-op.
    const isSelectEl0 = (targetEl as HTMLElement | null)?.tagName === 'SELECT'
    const selectRow = (!isSelectEl0 && el.closest
      ? (el.closest('[data-selected],[aria-checked],[data-tour-id^="project-owner-option"]') as HTMLElement | null)
      : null) || el
    let selObserver: MutationObserver | null = null
    const selectionTruthyNow = (): boolean => {
      const nodes: HTMLElement[] = [selectRow]
      try { nodes.push(...Array.from(selectRow.querySelectorAll<HTMLElement>('[data-selected],[aria-checked]'))) } catch { /* */ }
      return nodes.some(n => selectionIsTruthy(n.getAttribute('data-selected'), n.getAttribute('aria-checked')))
    }
    const rowHasSelectionAttr = (() => {
      try { return !!(selectRow.matches?.('[data-selected],[aria-checked]') || selectRow.querySelector?.('[data-selected],[aria-checked]')) } catch { return false }
    })()
    if (!isSelectEl0 && rowHasSelectionAttr) {
      try {
        selObserver = new MutationObserver(() => {
          if (advanced || cancelled) return
          if (Date.now() < ignoringInputUntil) return
          if (selectionTruthyNow()) setTimeout(trigger, 60)
        })
        selObserver.observe(selectRow, { attributes: true, subtree: true, attributeFilter: ['data-selected', 'aria-checked'] })
      } catch { /* */ }
    }

    // For native <select> elements, also listen for `change` events — picking
    // an option from the OS-level dropdown doesn't fire a click on the select
    // (or any DOM element we can observe), only `change` on the select itself.
    // If the step has a quoted/target_text expected value, only advance when
    // the selected option's text contains it (e.g. "Maxwell Ritchie").
    if ((targetEl as HTMLElement).tagName === 'SELECT') {
      const expectedRaw = extractExpectedValue(step) || step.target_text || ''
      const expected = expectedRaw.toLowerCase().replace(/\s+/g, ' ').trim()
      const changeHandler = (e: Event) => {
        if (advanced || cancelled) return
        if (!(e as any).isTrusted) return
        if (Date.now() < ignoringInputUntil) return
        if (expected) {
          const sel = targetEl as HTMLSelectElement
          const optText = (sel.options[sel.selectedIndex]?.text || '').toLowerCase().replace(/\s+/g, ' ').trim()
          if (!optText.includes(expected)) return
        }
        setTimeout(trigger, 80)
      }
      ;(targetEl as HTMLElement).addEventListener('change', changeHandler, true)
      const prevCleanup = cleanupCurrentStep
      cleanupCurrentStep = () => {
        try { prevCleanup() } catch { /* */ }
        try { (targetEl as HTMLElement).removeEventListener('change', changeHandler, true) } catch { /* */ }
      }
    }

    // Document-level capture-phase listener: address autocomplete (and similar
    // components) intercept the suggestion click and remove the suggestion DOM
    // node before our element-level listener can fire. Listening on document
    // ensures we still see the click even if the React handler removes the
    // target node afterwards.
    const docHandler = (e: Event) => {
      if (advanced || cancelled) return
      if (Date.now() < ignoringInputUntil) return
      const target = e.target
      if (!(target instanceof Element)) return
      // Skip clicks on the walkthrough's own UI
      if (target.closest('.kbwalk-popover') || target.closest('.kbwalk-ring') || target.closest('.kbwalk-backdrop-piece')) return
      // Match: click ON the highlighted element, its ancestor, OR an element
      // whose text matches the step's quoted/target_text. The latter handles
      // cases where the recorded selector resolves to a stale ref but the user
      // clicks the right semantic element (different DOM node, same text).
      let matched = false
      // For native <select> elements, ignore click advance — the click only
      // opens the dropdown; we advance via the `change` event when an option
      // is actually picked (handled separately above).
      const isSelect = (targetEl as HTMLElement | null)?.tagName === 'SELECT'
      if (!isSelect && targetEl && (target === targetEl || targetEl.contains(target) || target.contains(targetEl))) {
        matched = true
      }
      // Broaden to the whole selectable ROW: the resolved target is often the
      // owner-NAME leaf span, but the click lands on the checkbox/row body.
      if (!matched && !isSelect && selectRow && (target === selectRow || selectRow.contains(target))) {
        matched = true
      }
      if (!matched) {
        const quoted = step.text?.match(QUOTED_RE)?.[1]?.trim()
        const tt = step.target_text?.trim()
        const needle = (quoted && quoted.length >= 2 ? quoted : tt) || ''
        if (needle) {
          const n = needle.toLowerCase().replace(/\s+/g, ' ')
          const t = (target.textContent || '').trim().toLowerCase().replace(/\s+/g, ' ')
          if (t === n || (t.length > 0 && t.length < n.length * 4 && t.includes(n))) {
            matched = true
          }
        }
      }
      if (!matched) return
      // Live mode: clicks pass through to React naturally. The fetch
      // interceptor will snapshot any resulting writes for auto-undo on exit.
      setTimeout(trigger, 80)
    }
    document.addEventListener('click', docHandler, true)
    // Chain any prior cleanup (e.g. the native-<select> change listener) so it is
    // not dropped, and disconnect the selection observer on step change/teardown.
    const prevClickCleanup = cleanupCurrentStep
    cleanupCurrentStep = () => {
      try { prevClickCleanup() } catch { /* */ }
      document.removeEventListener('click', docHandler, true)
      if (selObserver) { try { selObserver.disconnect() } catch { /* */ } selObserver = null }
    }
  }

  const showStep = async () => {
    if (cancelled) return
    // Tear down the prior step's live-target tracking before resolving the next.
    stopRepositionTracking()
    const step = opts.steps[i]
    if (!step) { done(); return }

    // Type→Click hack: keep refocusing the prior Type input so autocomplete
    // suggestion dropdowns stay open long enough for the next step's resolver
    // to find the suggestion element.
    const newKind = deriveAction(step).kind
    stopRefocusInterval()
    if (
      pendingFocusKind === 'type' &&
      newKind === 'click' &&
      pendingFocusEl &&
      document.body.contains(pendingFocusEl)
    ) {
      const focusTarget = pendingFocusEl
      const startedAt = Date.now()
      refocusInterval = window.setInterval(() => {
        if (cancelled || !document.body.contains(focusTarget) || Date.now() - startedAt > 1500) {
          stopRefocusInterval()
          return
        }
        try { focusTarget.focus({ preventScroll: true } as FocusOptions) } catch { /* */ }
      }, 100)
    }
    pendingFocusEl = null
    pendingFocusKind = null

    const el = await resolveElementWithObserver(step, 10000, (fn) => { cancelPendingResolve = fn })
    // Element resolved (or timed out) — stop the refocus loop.
    stopRefocusInterval()
    cancelPendingResolve = () => {}
    if (cancelled) return
    currentEl = el
    if (el) {
      const isType = deriveAction(step).kind === 'type'
      // For Type steps: clear the input IMMEDIATELY on resolve, before the
      // popover even renders, so the user never sees stale state from a prior
      // walkthrough run interfering with the highlight or layout.
      if (isType) clearInputElement(el)
      try { el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }) } catch { /* */ }
      // Wait for scroll to settle then measure
      await new Promise(r => setTimeout(r, 200))
      if (cancelled) return
      const r = el.getBoundingClientRect()
      currentRect = r
      const cs = window.getComputedStyle(el)
      currentBorderRadius = cs.borderRadius && cs.borderRadius !== '0px' ? cs.borderRadius : '8px'
      renderPopoverContent(step, true)
      layoutBackdrop(r)
      layoutRing(r)
      layoutPopover(r)
      attachAutoAdvance(step, el)
      // Keep the spotlight pinned to the target's CURRENT rect through any
      // post-load reflow (async 360-page panels, etc.) until the step advances.
      startRepositionTracking(el)
      // For Type steps, auto-focus the input so the user can start typing immediately.
      if (isType) {
        setTimeout(() => { try { el.focus() } catch { /* */ } }, 50)
      }
    } else {
      currentRect = null
      currentBorderRadius = '8px'
      renderPopoverContent(step, false)
      layoutBackdrop(null)
      layoutRing(null)
      layoutPopover(null)
      // Resolve failed but we still keep the manual-continue fallback. For a
      // click step whose selector is a stable tour hook, ALSO install a
      // delegated document listener so a real click on that hook advances —
      // the user can perform the step manually and still progress even though
      // we couldn't anchor the highlight to it.
      if (deriveAction(step).kind === 'click' && isTourIdSelector(step.selector)) {
        const sel = step.selector as string
        let advanced = false
        const delegated = (e: Event) => {
          if (advanced || cancelled) return
          if (Date.now() < ignoringInputUntil) return
          const target = e.target
          if (!(target instanceof Element)) return
          if (target.closest('.kbwalk-popover') || target.closest('.kbwalk-ring') || target.closest('.kbwalk-backdrop-piece')) return
          let matched = false
          try { matched = !!target.closest(sel) } catch { /* invalid selector */ }
          if (!matched) return
          advanced = true
          setTimeout(() => { try { cleanupCurrentStep() } catch { /* */ } cleanupCurrentStep = () => {}; advance() }, 80)
        }
        document.addEventListener('click', delegated, true)
        cleanupCurrentStep = () => { document.removeEventListener('click', delegated, true) }
      }
    }
    fadeIn()
  }

  const advance = async () => {
    if (cancelled) return
    // Capture the prior Type step's input element BEFORE cleanup so we can
    // keep refocusing it during the next step's element-resolution window
    // (autocomplete dropdowns close on blur).
    const priorStep = opts.steps[i]
    const priorKind = priorStep ? deriveAction(priorStep).kind : null
    pendingFocusEl = (priorKind === 'type' && currentEl) ? currentEl : null
    pendingFocusKind = priorKind
    // Cleanup BEFORE re-resolving so the prior step's listener is gone before
    // we attach a new one (especially important when target element is reused).
    try { cancelPendingResolve() } catch { /* */ }
    cancelPendingResolve = () => {}
    try { cleanupCurrentStep() } catch { /* */ }
    cleanupCurrentStep = () => {}
    markAdvancedForCurrentStep = () => {}
    ignoringInputUntil = Date.now() + 200
    if (i >= opts.steps.length - 1) { done(); return }
    await fadeOut()
    if (cancelled) return
    i++
    showStep()
  }

  const back = async () => {
    if (cancelled) return
    // Cleanup BEFORE re-resolving the previous step's element so the current
    // step's auto-advance listener is removed first.
    try { cancelPendingResolve() } catch { /* */ }
    cancelPendingResolve = () => {}
    try { cleanupCurrentStep() } catch { /* */ }
    cleanupCurrentStep = () => {}
    markAdvancedForCurrentStep = () => {}
    ignoringInputUntil = Date.now() + 200
    if (i <= consumed) return
    await fadeOut()
    if (cancelled) return
    i = Math.max(consumed, i - 1)
    showStep()
  }

  // Initial layout (full backdrop while resolving)
  layoutBackdrop(null)

  // Consume any leading "Navigate to ..." steps silently. If none, leave the
  // user where they are — don't yank them to homepage just because they
  // started a walkthrough mid-task.
  ;(async () => {
    let didNavigate = false
    while (i < opts.steps.length) {
      const s = opts.steps[i]
      const m = s.text.match(NAV_RE)
      if (!m) break
      try { opts.navigate(pathFromUrl(m[1])) } catch { /* */ }
      didNavigate = true
      i++
      consumed++
      if (i < opts.steps.length && opts.steps[i].text.match(NAV_RE)) {
        await new Promise(r => setTimeout(r, 250))
        if (cancelled) return
      }
    }
    // Brief settle wait if we navigated, so the new page can render before
    // the first visible step's element resolver starts.
    if (didNavigate) await new Promise(r => setTimeout(r, 400))
    if (cancelled) return
    if (cancelled) return
    if (i >= opts.steps.length) { done(); return }
    // Coalesce consecutive Click+Type pairs targeting the same field.
    const remaining = coalesceSteps(opts.steps.slice(i))
    opts.steps = [...opts.steps.slice(0, i), ...remaining]
    showStep()
  })()

  return { cancel: () => exit() }
}
